/**
 * The talent drawers' two LIVE reads: the CheckUp calculation and the
 * paginated Activity timeline. (L2 · F26 · F20)
 *
 * Both follow state the server owns and the drawer merely renders. Kept apart
 * from `useBusiness.ts` because each carries a small state machine of its own —
 * a transition to watch, a page anchor to hold — and neither belongs in a
 * component.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { isCheckUpCalculationRunning } from '@tonnie/heroes';

import { useSdkContext } from '../providers/SdkProvider';
import {
  createBusinessService,
  type TalentActivityEvent,
  type TalentActivityFeed,
} from '../services/business.service';
import { businessKeys, useTalentActivity } from './useBusiness';

/** How often a running calculation is re-read. The brain job takes 30–90 s. */
const CALCULATION_POLL_MS = 3_000;

/** The per-talent keys this module reads — the same shape `useBusiness` uses. */
const drawerKeys = {
  checkup: (id: string, journeyId?: string) =>
    [...businessKeys.all, 'talent', id, 'checkup', journeyId ?? null] as const,
  checkupResults: (id: string, journeyId?: string) =>
    [...businessKeys.all, 'talent', id, 'checkup-results', journeyId ?? null] as const,
  activityPrefix: (id: string, journeyId?: string) =>
    [...businessKeys.all, 'talent', id, 'activity', journeyId ?? null] as const,
  /**
   * The OLDER pages of the timeline. Deliberately NOT under `activityPrefix`:
   * the SSE stream rewrites every entry under that prefix as a single-page
   * feed, and would corrupt a paged one.
   */
  activityOlder: (
    id: string,
    journeyId: string | undefined,
    limit: number,
    anchor: string | null
  ) =>
    [
      ...businessKeys.all,
      'talent',
      id,
      'activity-older',
      journeyId ?? null,
      limit,
      anchor,
    ] as const,
};

// ═══════════════════════════════════════════════════════════════════════════
// F26 — the CheckUp calculation, from the server
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Is this journey's CheckUp being calculated right now — whoever started it?
 *
 * The drawer used to derive "recalculating" itself, from a snapshot it took
 * when the ADMIN clicked Re-run and a poll that waited for `calculatedAt` to
 * move. So a calculation the CITIZEN started — the common case — never showed
 * at all, and one the admin started showed only in the tab that had clicked.
 * The server now says which job the assessment is on and what state it is in;
 * this reads that and nothing else.
 *
 * While a job runs, the read is repeated; the moment it reaches a terminal
 * state, the full results and the activity feed are invalidated so the new
 * score and its event appear without a reload.
 *
 * `calculating` is `false` when the `checkup` scope is withheld: the server
 * returns no calculation then, and the drawer renders the withheld notice
 * instead — never "calculating", never "not calculated".
 */
export function useTalentCheckupCalculation(talentId: string, options?: { journeyId?: string }) {
  const { client } = useSdkContext();
  const queryClient = useQueryClient();
  const businessService = createBusinessService(client);
  const journeyId = options?.journeyId;

  const query = useQuery({
    queryKey: drawerKeys.checkup(talentId, journeyId),
    queryFn: () => businessService.getTalentCheckup(talentId, { journeyId }),
    enabled: !!talentId,
    refetchInterval: (q) => {
      const status = q.state.data?.calculation?.status;
      return status && isCheckUpCalculationRunning(status) ? CALCULATION_POLL_MS : false;
    },
    refetchIntervalInBackground: false,
  });

  const calculation = query.data?.calculation ?? null;
  const calculating = calculation !== null && isCheckUpCalculationRunning(calculation.status);

  // The transition, not the state: a job that was running on the previous read
  // and is not on this one has just finished (or failed) — and what it wrote
  // is not in any cache yet.
  const wasCalculating = useRef(false);
  useEffect(() => {
    if (wasCalculating.current && !calculating) {
      void queryClient.invalidateQueries({
        queryKey: drawerKeys.checkupResults(talentId, journeyId),
      });
      void queryClient.invalidateQueries({
        queryKey: drawerKeys.activityPrefix(talentId, journeyId),
      });
    }
    wasCalculating.current = calculating;
  }, [calculating, queryClient, talentId, journeyId]);

  return { calculation, calculating, isLoading: query.isLoading };
}

// ═══════════════════════════════════════════════════════════════════════════
// F20 — the Activity timeline, all of it
// ═══════════════════════════════════════════════════════════════════════════

export interface TalentActivityTimeline {
  events: TalentActivityEvent[];
  /** `true` while a page older than what is shown exists. From the server's cursor. */
  hasMore: boolean;
  loadMore: () => void;
  isLoadingMore: boolean;
  /** The first page's consent answer (W8.D) — the feed-wide one. */
  irrEntitled: boolean | undefined;
  isLoading: boolean;
  error: unknown;
}

/**
 * The whole timeline, newest first, a page at a time.
 *
 * The drawer read ONE page of 50 and said "more activity" beneath it with no
 * way to fetch the rest — the cursor was on the wire and nothing used it.
 *
 * ─── Two reads, one contiguous list ─────────────────────────────────────
 *
 * The NEWEST page stays the live `useTalentActivity` query, because that is
 * the cache the SSE stream and the 60 s poll keep current. Older pages are an
 * infinite query that starts at the cursor the live page had when "load more"
 * was first pressed (`anchor`).
 *
 * New events push rows out of the live page's window. Those rows are older
 * than the new head and newer than the anchor, so neither read returns them —
 * they would silently vanish from a timeline the admin is reading. `retained`
 * keeps them: it is always the contiguous run from the newest row down to the
 * anchor row, rebuilt as `live page ++ (retained minus live page)` on every
 * refresh. That is correct only while the new live page OVERLAPS what was
 * retained; if more new rows arrived than one page holds, there is a gap no
 * read covers, and the only honest move is to re-anchor on the new live page
 * and let the older pages be fetched again.
 */
export function useTalentActivityTimeline(
  talentId: string,
  options: { journeyId?: string; limit: number }
): TalentActivityTimeline {
  const { client } = useSdkContext();
  const businessService = createBusinessService(client);
  const { journeyId, limit } = options;

  const head = useTalentActivity(talentId, { journeyId, limit });
  const [anchor, setAnchor] = useState<string | null>(null);
  const [retained, setRetained] = useState<TalentActivityEvent[]>([]);
  // The value the next refresh merges into, readable without re-running on it.
  const retainedRef = useRef<TalentActivityEvent[]>([]);
  const retain = (next: TalentActivityEvent[]) => {
    retainedRef.current = next;
    setRetained(next);
  };

  // A different talent or journey is a different timeline.
  useEffect(() => {
    setAnchor(null);
    retain([]);
  }, [talentId, journeyId]);

  const headEvents = head.data?.events;
  const headNextCursor = head.data?.nextCursor ?? null;
  useEffect(() => {
    if (anchor === null || !headEvents) return;
    const prev = retainedRef.current;
    const ids = new Set(headEvents.map((e) => e.id));
    if (!prev.some((e) => ids.has(e.id)) && headNextCursor) {
      // More arrived than one page holds: re-anchor rather than show a gap.
      retain(headEvents);
      setAnchor(headNextCursor);
      return;
    }
    retain([...headEvents, ...prev.filter((e) => !ids.has(e.id))]);
  }, [headEvents, headNextCursor, anchor]);

  const older = useInfiniteQuery({
    queryKey: drawerKeys.activityOlder(talentId, journeyId, limit, anchor),
    queryFn: ({ pageParam }) =>
      businessService.getTalentActivity(talentId, {
        journeyId,
        limit,
        cursor: pageParam,
      }),
    initialPageParam: anchor ?? '',
    getNextPageParam: (last: TalentActivityFeed) => last.nextCursor ?? undefined,
    enabled: !!talentId && anchor !== null,
  });

  const events = useMemo(() => {
    const top = anchor === null ? (headEvents ?? []) : retained;
    const seen = new Set(top.map((e) => e.id));
    const tail = (older.data?.pages ?? [])
      .flatMap((page) => page.events)
      .filter((e) => !seen.has(e.id) && (seen.add(e.id), true));
    return [...top, ...tail];
  }, [anchor, headEvents, retained, older.data]);

  const hasMore =
    anchor === null
      ? Boolean(headNextCursor)
      : older.data === undefined || Boolean(older.hasNextPage);

  const loadMore = () => {
    if (anchor === null) {
      if (!headNextCursor || !headEvents) return;
      retain(headEvents);
      setAnchor(headNextCursor);
      return;
    }
    if (older.hasNextPage && !older.isFetchingNextPage) void older.fetchNextPage();
  };

  return {
    events,
    hasMore,
    loadMore,
    isLoadingMore: older.isFetching,
    irrEntitled: head.data?.irrEntitled,
    isLoading: head.isLoading,
    error: head.error,
  };
}
